import { useEffect, useState } from "react";
import { AuthorSection, ChoiceSection, DualInputSection, Hero } from "./Argument";
import SavingsCalculator from "./Calculator";
import { CapabilitiesSection, LanguagesSection, QualitySection } from "./Capabilities";
import {
  IntegrationSection,
  PricingSection,
  RolloutSection,
  SecuritySection,
} from "./Compliance";
import { CustomBuildSection } from "./CustomBuild";
import { ResultsSection } from "./Evidence";
import { InvestigationsSection } from "./Investigations";
import { ContactSection, RecommendationsSection } from "./Recommendations";
import { RFI } from "./data";
import { Section } from "./ui";

/**
 * Interactive briefing for the San Diego Police Department, produced as the
 * companion to our response to RFI 30000072-27-E.
 *
 * Served at /sdpd, wrapped in ForceLightTheme (see client/src/index.tsx),
 * because this is a shareable external document rather than part of the
 * themed application.
 *
 * Every figure on this page traces to a citation in ./data.ts. See the header
 * comment there before adding anything.
 */

type NavItem = { id: string; label: string; num?: string };

const NAV: NavItem[] = [
  { id: "top", label: "Overview" },
  { id: "choice", label: "The choice", num: "01" },
  { id: "author", label: "The officer is the author", num: "02" },
  { id: "dual-input", label: "Two recordings, one report", num: "03" },
  { id: "capabilities", label: "Capabilities", num: "04" },
  { id: "quality", label: "Report quality", num: "05" },
  { id: "languages", label: "Language coverage", num: "06" },
  { id: "calculator", label: "What it is worth", num: "07" },
  { id: "results", label: "Deployment results", num: "08" },
  { id: "security", label: "Security & compliance", num: "09" },
  { id: "integration", label: "Integration", num: "10" },
  { id: "investigations", label: "Jail call intelligence", num: "11" },
  { id: "custom", label: "End-of-year reports", num: "12" },
  { id: "rollout", label: "Implementation", num: "13" },
  { id: "pricing", label: "Pricing structure", num: "14" },
  { id: "rfp", label: "Recommendations for the RFP", num: "15" },
  { id: "contact", label: "Next steps" },
];

// Stable identity. Passing NAV.map(...) inline would rebuild the array every
// render and re-subscribe the IntersectionObserver on each one.
const NAV_IDS = NAV.map((n) => n.id);

export default function SDPDBriefing() {
  const active = useScrollSpy(NAV_IDS);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <TopBar />

      <div className="max-w-[1180px] mx-auto px-5 md:px-8">
        <div className="lg:grid lg:grid-cols-[210px_minmax(0,1fr)] lg:gap-12">
          <SideNav active={active} />

          <main id="top" className="min-w-0 pb-24">
            <Hero />
            <ChoiceSection />
            <AuthorSection />
            <DualInputSection />

            <CapabilitiesSection />
            <QualitySection />
            <LanguagesSection />

            <Section
              id="calculator"
              number="07"
              title="What it is worth to San Diego"
              subtitle={
                <>
                  Every number below is editable. The defaults are drawn from SDPD's own
                  published budget and from what three agency deployments actually
                  produced. They are our assumptions, not your data. Change them and
                  the model changes with them.
                </>
              }
            >
              <SavingsCalculator />
            </Section>

            <ResultsSection />
            <SecuritySection />
            <IntegrationSection />
            <InvestigationsSection />
            <CustomBuildSection />
            <RolloutSection />
            <PricingSection />
            <RecommendationsSection />
            <ContactSection />

            <FooterNote />
          </main>
        </div>
      </div>
    </div>
  );
}

// ===================================================================
// Chrome
// ===================================================================

function TopBar() {
  return (
    <div className="border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-30 print:static">
      <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-bold tracking-tight text-slate-900 shrink-0">
            Police Narratives AI
          </span>
          <span className="hidden sm:block text-slate-300">|</span>
          <span className="hidden sm:block text-sm text-slate-500 truncate">
            Prepared for the {RFI.department}
          </span>
        </div>
        <div className="text-[11px] font-mono text-slate-400 shrink-0 hidden md:block">
          RFI {RFI.number}
        </div>
      </div>
    </div>
  );
}

function SideNav({ active }: { active: string }) {
  return (
    <nav
      aria-label="Contents"
      className="hidden lg:block sticky top-[57px] self-start max-h-[calc(100vh-57px)] overflow-y-auto py-10 print:hidden"
    >
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-3">
        Contents
      </div>
      <ol className="space-y-0.5">
        {NAV.map((n) => {
          const isActive = active === n.id;
          return (
            <li key={n.id}>
              <a
                href={`#${n.id}`}
                className={`flex gap-2 py-1.5 text-[13px] leading-snug transition-colors border-l-2 pl-3 -ml-px ${
                  isActive
                    ? "border-blue-600 text-blue-700 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-900 hover:border-slate-300"
                }`}
              >
                {n.num && (
                  <span className="font-mono text-[11px] tabular-nums opacity-60 shrink-0 pt-px">
                    {n.num}
                  </span>
                )}
                <span>{n.label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function FooterNote() {
  return (
    <footer className="mt-20 pt-8 border-t border-slate-200">
      <p className="text-xs text-slate-500 leading-relaxed max-w-3xl">
        Prepared by Police Narratives AI in response to {RFI.agency} Request for
        Information {RFI.number}, {RFI.title}. Figures attributed to the City of San Diego
        are drawn from published budget documents; figures describing platform results are
        modeled from deployment data and are labeled as such throughout. This briefing
        accompanies our written response submitted through PlanetBids.
      </p>
      <p className="text-xs text-slate-400 mt-4">
        Questions: <span className="text-slate-600">michael@policenarratives.ai</span>
      </p>
    </footer>
  );
}

// ===================================================================
// Scroll spy
// ===================================================================

function useScrollSpy(ids: string[]): string {
  const [active, setActive] = useState(ids[0] ?? "");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Choose the entry nearest the top of the viewport among those visible.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActive(visible[0].target.id);
      },
      // Bias the band toward the upper third so a heading activates as it
      // arrives rather than when it reaches the middle of the screen.
      { rootMargin: "-10% 0px -70% 0px", threshold: 0 }
    );

    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter((n): n is HTMLElement => n !== null);
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
